<template>
  <v-form class="org-profile-form">
    <v-container fluid class="px-10 pt-10">
      <v-row justify-md="space-between" align="center">
        <v-col class="text-center text-md-left" cols="12" md="6">
          <h3 class="org-profile-form__title">{{ stepper ? "Actualizar" : "Editar" }} mi página</h3>
        </v-col>
        <v-col class="text-center text-md-right" cols="12" md="6">
          <OrganizationProfilePreview
            :key="previewKey"
            :organization="organization"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="8">
          <!-- Nombre -->
          <FormQuestionTitle>
            Nombre de mi organización
            <template #tooltip>
              Indica el nombre bajo el cual quieres que sea identificada tu Organización.
            </template>
          </FormQuestionTitle>
          <v-text-field
            class="org-profile-form__input"
            ref="organizationName"
            name="organizationName"
            placeholder="Ingresa el nombre"
            hint="Obligatorio"
            persistent-hint
            v-model="organization.name"
            required
            :error-messages="organizationNameErrors"
            @input="$v.organization.name.$touch()"
            @blur="$v.organization.name.$touch()"
          />

          <!-- Sitio web -->
          <FormQuestionTitle>
            Sitio web de mi organización
            <template #tooltip>
              Ingresa link al web site de tu organización.
            </template>
          </FormQuestionTitle>
          <v-text-field
            class="org-profile-form__input"
            ref="organizationWeb"
            name="organizationWeb"
            placeholder="Ingresa el link"
            hint="Obligatorio"
            persistent-hint
            v-model="organization.networks.web"
            required
            :error-messages="organizationWebErrors"
            @input="$v.organization.networks.web.$touch()"
            @blur="$v.organization.networks.web.$touch()"
          />

          <!-- Logo -->
          <FormQuestionTitle>
            Logo
            <template #tooltip>
              Ingresa el logo de tu organización en formato cuadrado [mínimo 200x200px], 
              idealmente fondo blanco. Puedes usar el que tienes en Instagram o Linkedin.
            </template>
          </FormQuestionTitle>
          <ImageLoader
            id="organizationLogo"
            name="organizationLogo"
            v-model="organization.media.logo"
            cols="10"
            class="col-12 col-sm-8 col-md-8 col-lg-6 pa-0"
            path="uploads/images/logos/"
            @img-load="onImgLoad"
          />

          <!-- Foto de portada -->
          <FormQuestionTitle>
            Foto de portada
            <template #tooltip>
              Elige una imagen que represente a tu organización. Esto ayudará a
              los candidatos a conectarse mejor con ustedes.
            </template>
          </FormQuestionTitle>
          <ImageLoader
            id="organizationCover"
            name="organizationCover"
            v-model="organization.media.cover"
            cols="10"
            height="250"
            ratio="3"
            path="uploads/images/covers/"
            @img-load="onImgLoad"
          />

          <!-- Teléfono -->
          <FormQuestionTitle>
            Teléfono
          </FormQuestionTitle>
          <v-text-field
            class="org-profile-form__input"
            name="phone"
            placeholder=""
            hint="Opcional"
            persistent-hint
            v-model="organization.phone"
            :error-messages="organizationPhoneErrors"
            @input="$v.organization.phone.$touch()"
            @blur="$v.organization.phone.$touch()"
          />   

          <!-- Ciudad -->
          <FormQuestionTitle>
            Ciudad
            <template #tooltip>
              Indica la ciudad de tu organización.
            </template>
          </FormQuestionTitle>
          <v-select
            class="org-profile-form__input"
            ref="organizationCity"
            name="organizationCity"
            placeholder="Selecciona la ciudad"
            hint="Obligatorio"
            persistent-hint
            v-model="organization.city"
            :items="getCities"
            required
            :error-messages="organizationCityErrors"
            @input="$v.organization.city.$touch()"
            @blur="$v.organization.city.$touch()"
          />
          
          <!-- Industria -->
          <FormQuestionTitle>
            Industria
            <template #tooltip>
              Selecciona la industria que mejor identifique a tu Organización.
            </template>
          </FormQuestionTitle>
          <v-select
            class="org-profile-form__input"
            ref="organizationSegment"
            name="organizationSegment"
            placeholder="Selecciona la industria"
            hint="Obligatorio"
            persistent-hint
            v-model="organization.segment"
            :items="getSegments"
            required
            :error-messages="organizationSegmentErrors"
            @input="$v.organization.segment.$touch()"
            @blur="$v.organization.segment.$touch()"
          />

          <!-- Colaboradores -->
          <FormQuestionTitle>
            Tamaño de mi organización
            <template #tooltip>
              Mueve los extremos de la barra para indicar un rango
              del número de personas que trabajan en tu Organización.
            </template>
          </FormQuestionTitle>
          <v-range-slider
            v-model="organization.employees"
            :max="size[1]"
            :min="size[0]"
            class="align-center pt-12"
            thumb-label="always"
          />

          <!-- Igualdad de género -->
          <FormQuestionTitle>
            Política de igualdad de género
            <template #tooltip>
              Cuéntale a los postulantes si ya tienen una política
              de igualdad de género explícita y formalizada.
              Si está en proceso de desarrollo o es algo que aún no se trabaja.
            </template>
          </FormQuestionTitle>
          <v-select
            class="org-profile-form__input"
            ref="organizationGender"
            name="organizationGender"
            placeholder="Política de igualdad de género"
            hint="Opcional"
            persistent-hint
            v-model="organization.gender"
            :items="getGenders"
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- Sección propósito -->
    <v-container fluid class="org-profile-form__purpose-section px-10">
      <v-row>
        <v-col cols="12" md="8">          
          <!-- Propósito -->
          <FormQuestionTitle margin>
            ¿Cuál es el propósito de tu organización?
            <template #tooltip>
              Describe brevemente el propósito, para que puedas atraer a los
              mejores talentos. El propósito es <strong>eso que inspira y da
              sentido a todo lo que hacen en tu Organización</strong>.
              <br/>
              El nuestro, por ejemplo, es impulsar la transformación de personas
              y organizaciones hacia la economía del bien común.
            </template>
          </FormQuestionTitle>
          <tiptap-vuetify
            ref="organizationPurpose"
            name="organizationPurpose"
            v-model="organization.purpose"
            placeholder="Escribe aquí el propósito de tu organización"
            :extensions="extensions"
            required
            :error-messages="organizationPurposeErrors"
            @input="$v.textValidate.purpose.$touch()"
            @blur="$v.textValidate.purpose.$touch()"
          />
          <div class="v-text-field__details">
            <div class="v-messages theme--light">
              <div class="v-messages__wrapper d-flex justify-space-between">
                <div
                  :class="`v-messages__message ${ organizationPurposeErrors.length !== 0 ? 'red--text' : ''}`"
                >
                  Obligatorio
                </div>
                <span>{{getInnerText(organization.purpose).length}}/400</span>
              </div>
              <div :class="`v-messages__message ${ organizationPurposeErrors.length !== 0 ? 'red--text' : ''}`">
                <span v-for="(item, index) in organizationPurposeErrors" :key="index">{{ item }}</span>
              </div>
            </div>
          </div>

          <!-- Logro -->
          <FormQuestionTitle margin>
            Cuéntanos de algún logro reciente o una meta de tu organización,
            en el camino hacia ese propósito.
            <template #tooltip>
              El objetivo de esto es que los candidatos que postulen a tu
              organización, puedan visualizar el impacto de lo que Uds hacen.
              <br />
              Por ejemplo, uno de nuestros logros es que hemos conectado a más de
              6000 personas con un trabajo con sentido.
            </template>
          </FormQuestionTitle>
          <tiptap-vuetify
            ref="organizationAchievement"
            name="organizationAchievement"
            v-model="organization.achievement"
            placeholder="Escribe aquí el propósito de tu organización"
            :extensions="extensions"
            required
            :error-messages="organizationAchievementErrors"
            @input="$v.textValidate.achievement.$touch()"
            @blur="$v.textValidate.achievement.$touch()"
          />
          <div class="v-text-field__details">
            <div class="v-messages theme--light">
              <div class="v-messages__wrapper d-flex justify-space-between">
                <div :class="`v-messages__message ${ organizationAchievementErrors.length !== 0 ? 'red--text' : ''}`">Obligatorio</div>
                <span>{{getInnerText(organization.achievement).length}}/220</span>
              </div>
              <div :class="`v-messages__message ${ organizationAchievementErrors.length !== 0 ? 'red--text' : ''}`">
                <span v-for="(item, index) in organizationAchievementErrors" :key="index">{{ item }}</span>
              </div>
            </div>
          </div>

          <!-- Cualidades -->
          <FormQuestionTitle margin>
            Háblanos un poco de tu organización y de por qué es un buen lugar para trabajar.
            <template #tooltip>
              Describe tu Organización contando una buena historia o lo que
              consideres la representará de mejor forma.
              <br />
              Recuerda que con esta información estás buscando atraer a los mejores
              talentos; esos que están buscando una oportunidad para impactar.
            </template>
          </FormQuestionTitle>
          <tiptap-vuetify
            ref="organizationDescription"
            name="organizationDescription"
            v-model="organization.description"
            placeholder="Escribe acá"
            :extensions="extensions"
            required
            :error-messages="organizationDescriptionErrors"
            @input="$v.textValidate.description.$touch()"
            @blur="$v.textValidate.description.$touch()"
          />
          <div class="v-text-field__details">
            <div class="v-messages theme--light">
              <div class="v-messages__wrapper d-flex justify-space-between">
                <div :class="`v-messages__message ${ organizationDescriptionErrors.length !== 0 ? 'red--text' : ''}`">Obligatorio</div>
                <span>{{getInnerText(organization.description).length}}/450</span>
              </div>
              <div :class="`v-messages__message ${ organizationDescriptionErrors.length !== 0 ? 'red--text' : ''}`">
                <span v-for="(item, index) in organizationDescriptionErrors" :key="index">{{ item }}</span>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid class="px-10">
      <v-row>
        <v-col cols="12" md="8">          
          <!-- Video -->
          <FormQuestionTitle>
            Video que complemente tu descripción.
            <template #tooltip>
              Puedes agregar un link a un video YouTube u otra platforma
              abierta, para que los candidatos conozcan más de tu Organización.
            </template>
          </FormQuestionTitle>
          <v-text-field
            class="org-profile-form__input"
            ref="organizationVideo"
            name="organizationVideo"
            placeholder="Ingresa link"
            hint="Opcional"
            persistent-hint
            v-model="organization.media.video"
          />

          <!-- ODS -->
          <FormQuestionTitle>
            Marca a continuación los 4 principales Objetivo de Desarrollo
            Sostenible (ODS) con los que se identifique tu organización (opcional)
            <template #indications>
              Si no sabes lo que son los Objetivos de Desarrollo Sostenible, haz 
              <a
                href="https://www.pactoglobal.cl/objetivos-de-desarrollo-sostenible/conoce-los-ods/"
                target="_blank"
              >
                click acá
              </a>
            </template>
          </FormQuestionTitle>
          <Goals cols="2" :goals="organization.goals" @click="goalsSelected" />

          <!-- RRSS -->
          <FormQuestionTitle margin>
            Redes sociales
            <template #tooltip>
              Ingresa perfiles de RRSS para que los candidatos puedan seguirlos
              y saber más de tu Organización.
            </template>
          </FormQuestionTitle>
          <v-row class="justify-md-start justify-center">
            <v-col class="py-1" cols="6">
              <v-text-field
                class="org-profile-form__input"
                ref="linkedin"
                name="linkedin"
                placeholder="LinkedIn"
                hint="Opcional"
                persistent-hint
                v-model="organization.networks.linkedin"
                prepend-icon="mdi-linkedin"
                @input="$v.organization.networks.linkedin.$touch()"
                @blur="$v.organization.networks.linkedin.$touch()"
              />
            </v-col>
            <v-col class="py-1" cols="6">
              <v-text-field
                class="org-profile-form__input"
                ref="twitter"
                name="twitter"
                placeholder="Twitter"
                hint="Opcional"
                persistent-hint
                v-model="organization.networks.twitter"
                prepend-icon="mdi-twitter"
                @input="$v.organization.networks.twitter.$touch()"
                @blur="$v.organization.networks.twitter.$touch()"
              />
            </v-col>
            <v-col class="py-1" cols="6">
              <v-text-field
                class="org-profile-form__input"
                ref="facebook"
                name="facebook"
                placeholder="Facebook"
                hint="Opcional"
                persistent-hint
                v-model="organization.networks.facebook"
                prepend-icon="mdi-facebook"
                @input="$v.organization.networks.facebook.$touch()"
                @blur="$v.organization.networks.facebook.$touch()"
              />
            </v-col>
            <v-col class="py-1" cols="6">
              <v-text-field
                class="org-profile-form__input"
                ref="instagram"
                name="instagram"
                placeholder="Instagram"
                hint="Opcional"
                persistent-hint
                v-model="organization.networks.instagram"
                prepend-icon="mdi-instagram"
                @input="$v.organization.networks.instagram.$touch()"
                @blur="$v.organization.networks.instagram.$touch()"
              />
            </v-col>
          </v-row>

          <!-- Acciones -->
          <v-card-actions class="text-center px-3 mt-16 mb-8">
            <v-row class="justify-md-start justify-center">
              <OrganizationProfilePreview
                :key="previewKey"
                :organization="organization"
              />
              <v-btn
                class="org-profile-form__btn"
                color="info"
                large
                button
                :loading="loading"
                :disabled="$v.$invalid || loading"
                @click="updateProfile"
              >
                ACTUALIZAR {{ stepper ? 'Y CONTINUAR' : '' }}
              </v-btn>
            </v-row>
          </v-card-actions>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import { required, minLength, maxLength, numeric, integer } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { handleTxt } from "@/libs/mixins";
import { fixturesComputed, userComputed } from "@/store/helpers.js";
import FormQuestionTitle from "@/components/FormQuestionTitle";
import Goals from "@/components/Goals";
import ImageLoader from "@/components/ImageLoader";
import OrganizationProfilePreview from "@/components/OrganizationProfilePreview";
import { TiptapVuetify, Bold, Italic, Underline, BulletList, OrderedList, ListItem, Link, History } from "tiptap-vuetify";

export default {
    name: "OrganizationProfileForm",

    components: {
        FormQuestionTitle,
        Goals,
        ImageLoader,
        OrganizationProfilePreview,
        TiptapVuetify,
    },

    props: {
        stepper: {
            type: Boolean,
            default: false,
        }
    },

    mixins: [validationMixin, handleTxt],

    data() {
        return {
            errors: [],
            extensions: [
                Bold,
                Italic,
                Underline,
                Link,
                ListItem,
                BulletList,
                OrderedList,
                History
            ],
            loading: false,
            organization: {
                name: "",
                alias: "",
                phone: "",
                city: "",
                segment: "",
                gender: [],
                description: "",
                purpose: "",
                achievement: "",
                goals: [],
                media: {
                    logo: "",
                    cover: "",
                    video: ""
                },
                networks: {
                    web: "",
                    linkedin: "",
                    facebook: "",
                    twitter: "",
                    instagram: "",
                },
                employees: [1, 10]
            },
            size: [1, 100],
            textValidate: {
                description: "",
                purpose: "",
                achievement: "",
            },
            previewKey: new Date().getTime(),
        };
    },

    computed: {
        ...fixturesComputed,
        ...userComputed,
        organizationNameErrors() {
            const errors = [];
            if (!this.$v.organization.name.$dirty) return errors;
            !this.$v.organization.name.required && errors.push("Nombre organización requerido");
            return errors;
        },
        organizationWebErrors() {
            const errors = [];
            if (!this.$v.organization.networks.web.$dirty) return errors;
            !this.$v.organization.networks.web.required && errors.push("Web organización requerida");
            return errors;
        },
        organizationCountryErrors() {
            const errors = [];
            if (!this.$v.organization.country.$dirty) return errors;
            !this.$v.organization.country.required && errors.push("Pais organización requerido");
            return errors;
        },
        organizationPhoneErrors() {
            const errors = [];
            if (!this.$v.organization.phone.$dirty) return errors;
            !this.$v.organization.phone.numeric && !this.$v.organization.phone.integer && errors.push("Solo números enteros");
            !this.$v.organization.phone.maxLength && errors.push("Son muchos dígitos");
            return errors;
        },
        organizationCityErrors() {
            const errors = [];
            if (!this.$v.organization.city.$dirty) return errors;
            !this.$v.organization.city.required && errors.push("Ciudad organización requerida");
            return errors;
        },
        organizationSegmentErrors() {
            const errors = [];
            if (!this.$v.organization.segment.$dirty) return errors;
            !this.$v.organization.segment.required && errors.push("Segmento requerido");
            return errors;
        },
        organizationDescriptionErrors() {
            const errors = [];
            if (!this.$v.textValidate.description.$dirty) return errors;
            !this.$v.textValidate.description.required && errors.push("Descripcion requerida");
            !this.$v.textValidate.description.minLength && errors.push("El texto debe tener al menos 100 caracteres");
            !this.$v.textValidate.description.maxLength && errors.push("El texto debe tener un máximo de 450 caracteres");
            return errors;
        },
        organizationPurposeErrors() {
            const errors = [];
            if (!this.$v.textValidate.purpose.$dirty) return errors;
            !this.$v.textValidate.purpose.required && errors.push("Proposito requerido");
            !this.$v.textValidate.purpose.minLength && errors.push("El texto debe tener al menos 30 caracteres");
            !this.$v.textValidate.purpose.maxLength && errors.push("El texto debe tener un máximo de 400 caracteres");
            return errors;
        },
        organizationAchievementErrors() {
            const errors = [];
            if (!this.$v.textValidate.achievement.$dirty) return errors;
            !this.$v.textValidate.achievement.required && errors.push("Logro requerido");
            !this.$v.textValidate.achievement.minLength && errors.push("El texto debe tener al menos 80 caracteres");
            !this.$v.textValidate.achievement.maxLength && errors.push("El texto debe tener un máximo de 220 caracteres");
            return errors;
        }
    },

    methods: {
        goalsSelected(values) {
            this.organization.goals = values;
        },
        updateProfile: function(e) {
            if (this.stepper) {
                this.$emit("forward-step");
                window.scrollTo(0, 0);
            }
            this.loading = true;
            this.$store.dispatch("users/updateOrganization", this.organization).then(() => {
                this.loading = false;
                if (!this.stepper) {
                    this.$router.push({ name: "RedirectionRule" });
                }
            }).catch(err => {
                this.loading = false;
                this.errors = err;
            });
        },
        onImgLoad(evt) {
            this.previewKey = new Date().getTime();
        },
    },

    validations: {
        organization: {
            phone: { numeric, integer, maxLength: maxLength(30) },
            name: { required, minLength: minLength(3) },
            city: { required },
            segment: { required },
            media: {
                logo: { required: true },
                cover: { required: false },
                video: { required: false }
            },
            networks: {
                web: { required, minLength: minLength(3) },
                linkedin: { required: false },
                facebook: { required: false },
                twitter: { required: false },
                instagram: { required: false }
            }
        },
        // Se agrega validacion para texto que viene con etiquetas html
        textValidate: {
            description: { required, minLength: minLength(100), maxLength: maxLength(450) },
            purpose: { required, minLength: minLength(30), maxLength: maxLength(400) },
            achievement: { required, minLength: minLength(80), maxLength: maxLength(220) },
        }

    },

    watch: {
        "organization.description"() {
            this.textValidate.description = this.getInnerText(this.organization.description);
        },
        "organization.purpose"() {
            this.textValidate.purpose = this.getInnerText(this.organization.purpose);
        },
        "organization.achievement"() {
            this.textValidate.achievement = this.getInnerText(this.organization.achievement);
        },
    },

    created() {
        let organization = this.getUser.organization;
        let user = this.getUser;
        this.organization = {
            ...this.organization,
            ...organization
        };
        if (organization) this.$v.$touch();
        this.organization.employees = this.organization.employees.length === 0 
            ? [1, 10] 
            : this.organization.employees;

        // Se definen nombre de logo
        if (!this.organization.logo) {
            this.organization.media.logo = this.organization.id;
        }
        // se define nombre de cover
        if (!this.organization.media.cover) {
            this.organization.media.cover = this.organization.id;
        }
    },
};
</script>
